import { IAuthServiceV2 } from '@volkswagen-onehub/authservice';
import { UnknownSimStatusError } from '../errors/UnknownSimStatusError';
import { LoggingFacade } from '@vwdfive/myvw-logging';

export interface UserStatus {
    guestUserEnrollment: {
        status: string;
        vehicleCapabilities: string;
        vehicleCapabilitiesStatus: boolean;
    };
    profileEnrichment: {
        personalDataCompleted: boolean;
        spinCompleted: boolean;
        status: string;
        termsAndConditionsAccepted: boolean;
    };
    userIdentification: {
        status: string;
    };
    vehiclesEnrollments: [
        {
            HasPrimaryUser: boolean;
            enrollmentStatus: string;
            isHg3CapabilitiesAvailable: boolean;
            isSpinResetFlow: boolean;
            status: string;
            vehicleCapabilities: string;
            vehicleCapabilitiesStatus: boolean;
            verificationMode: string;
            vin: string;
        },
    ];
}

export type UserIdentStatus = Pick<UserStatus, 'userIdentification' | 'vehiclesEnrollments'>;

interface IPostData {
    success: string;
    vin: string;
    requestedSteps: string;
    cancelUrl?: string;
}

export class VasClient {
    private readonly resourceHostId = `myvw-vas-${this.stage}`;

    constructor(
        private readonly authServiceV2: IAuthServiceV2,
        private readonly loggingFacade: LoggingFacade,
        private readonly stage: string,
    ) {}

    public async getEnrollmentUrl(
        vin: string,
        successUrl: string,
        requestedSteps?: string,
        cancelUrl?: string,
    ): Promise<string> {
        return this.getUrlFor(vin, successUrl, requestedSteps || 'VW_PE_VE_1', cancelUrl);
    }

    public async getProfileEnrichmentUrl(vin: string, successUrl: string, requestedSteps?: string): Promise<string> {
        return this.getUrlFor(vin, successUrl, requestedSteps || 'VW_PE_1');
    }

    private async getUrlFor(
        vin: string,
        successUrl: string,
        requestedSteps: string,
        cancelUrl?: string,
    ): Promise<string> {
        const query = `auth/users/__userId__/session`;
        const postDataObj: IPostData = {
            success: successUrl,
            vin,
            requestedSteps,
            cancelUrl: cancelUrl || undefined,
        };
        const postData = JSON.stringify(postDataObj);

        try {
            const response = await this.authServiceV2.post(this.resourceHostId, query, postData,
                {'Content-Type': 'application/json'});
            if (response.headers.has('Location')) {
                return response.headers.get('Location') as string;
            }
            // TODO: Clean this code smell
            throw new Error('There is not Location in the VAS headers response');

        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    public async getUserStatus(vin: string): Promise<UserStatus> {
        const query = `auth/v2/users/__userId__/status?vin=${vin}&requestedSteps=VW_Dynamic_1`;

        try {
            const response = await this.authServiceV2.get(this.resourceHostId, query);

            return await response.json() as UserStatus;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    public async getUserIdentStatus(vin: string): Promise<UserIdentStatus> {
        const query = `auth/v2/users/__userId__/status/ident?vin=${vin}`;

        try {
            const response = await this.authServiceV2.get(this.resourceHostId, query);
            return await response.json() as UserIdentStatus;
        } catch (error: any) {
            throw this.handleError(error);
        }
    }

    private handleError(error: any) {
        const errorCode = error.errorDetail?.errorCode;

        if (error.status === 400 && errorCode === '1009') {
            error = new UnknownSimStatusError();
        }

        const errorMessage = errorCode
            ? `VAS call failed with error code ${errorCode}`
            : 'VAS call failed. No error code provided.';
        this.loggingFacade.error(errorMessage, error);
        
        return error;
    }
}
